import * as THREE from 'three';
import type { VehicleDef } from '../config/vehicles';
import type { UpgradeLevels } from '../sim/upgrades';
import { instantiate, type ModelBatch, type UpgradeParts, type VehicleModels } from './assets';

/** Upgrade level at which a part becomes visible on the vehicle. */
const VISIBLE_AT = 3;
/** The upgrade parts are authored for the pickup's wheel radius. */
const PART_WHEEL_RADIUS = 0.42;
const COILOVER_LENGTH = 0.42;
const GHOST_TINT = new THREE.Color(0x9fd8ff);

/**
 * A delivery vehicle (Kiln GLBs). The body is posed from the physics chassis; each physics wheel
 * drives one visual wheel (single-track bikes) or a pair (cars and trucks), posed in chassis space
 * from the suspension state. Upgrades at level 3+ show on the vehicle: off-road tyres, coilovers,
 * a roof rack with jerry cans, an exhaust stack and a hood scoop.
 */
export class BuggyView {
  readonly chassis = new THREE.Group();
  private readonly axles: THREE.Group[][] = [[], []];
  private readonly coilovers: THREE.Group[] = [];
  private readonly flashMaterials: THREE.MeshStandardMaterial[] = [];

  constructor(
    models: VehicleModels,
    private readonly vehicle: VehicleDef,
    parts: UpgradeParts | null,
    levels: Readonly<UpgradeLevels>,
  ) {
    const body = instantiate(models.body);
    // Crash feedback: give the body's own (cloned) materials a red emissive pulse.
    body.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh) return;
      const material = (mesh.material as THREE.MeshStandardMaterial).clone();
      mesh.material = material;
      this.flashMaterials.push(material);
    });
    this.chassis.add(body);
    if (vehicle.view.lettering) this.addLettering(vehicle.view.lettering);
    const v = vehicle.view;
    const offroad = parts !== null && levels.tyres >= VISIBLE_AT;
    const sides = v.wheelTrackZ === 0 ? [0] : [1, -1];
    for (let i = 0; i < 2; i++) {
      for (const side of sides) {
        const wheel = offroad ? instantiate(parts.tyre) : instantiate(models.wheel);
        if (offroad) wheel.scale.setScalar(vehicle.wheel.radius / PART_WHEEL_RADIUS);
        wheel.userData.side = side;
        this.axles[i].push(wheel);
        this.chassis.add(wheel);
      }
    }
    if (parts) this.addUpgradeParts(parts, levels);
  }

  /** Turns this view into a ghost: every material becomes a translucent clone, no shadows. */
  makeGhost(opacity = 0.38): void {
    const clones = new Map<THREE.Material, THREE.Material>();
    this.chassis.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh) return;
      const source = mesh.material as THREE.Material;
      let ghost = clones.get(source);
      if (!ghost) {
        ghost = source.clone();
        // A pale-blue tint so the ghost never reads as the player's own vehicle.
        const tinted = ghost as THREE.MeshStandardMaterial;
        if (tinted.color) tinted.color.lerp(GHOST_TINT, 0.55);
        if (tinted.emissive) tinted.emissive.copy(GHOST_TINT).multiplyScalar(0.18);
        ghost.transparent = true;
        ghost.opacity = opacity * (source.transparent ? source.opacity : 1);
        ghost.depthWrite = false;
        clones.set(source, ghost);
      }
      mesh.material = ghost;
      mesh.castShadow = false;
      mesh.renderOrder = 2;
    });
    for (const m of this.flashMaterials) m.dispose();
    this.flashMaterials.length = 0;
    for (const m of clones.values()) this.flashMaterials.push(m as THREE.MeshStandardMaterial);
  }

  /** Fades the whole view (ghost finished its run). */
  setOpacity(scale: number): void {
    this.chassis.traverse((node) => {
      const mesh = node as THREE.Mesh;
      if (!mesh.isMesh) return;
      const m = mesh.material as THREE.Material;
      m.userData.baseOpacity ??= m.opacity;
      m.opacity = (m.userData.baseOpacity as number) * scale;
    });
  }

  addTo(scene: THREE.Object3D): void {
    scene.add(this.chassis);
  }

  dispose(): void {
    this.chassis.removeFromParent();
    for (const m of this.flashMaterials) m.dispose();
  }

  setWheel(i: number, x: number, y: number, spin: number): void {
    const track = this.vehicle.view.wheelTrackZ;
    for (const wheel of this.axles[i]) {
      wheel.position.set(x, y, track * (wheel.userData.side as number));
      wheel.rotation.set(0, 0, -spin); // spin grows while rolling forward (+X), i.e. clockwise seen from +Z
    }
    // Coilovers run from the wheel hub up to a fixed mount on the body.
    const coil = this.coilovers[i];
    if (coil) {
      const top = this.vehicle.wheel.axleY + this.vehicle.suspension.restLength + 0.12;
      coil.position.set(x, y, coil.position.z);
      coil.scale.set(1, Math.max(0.3, (top - y) / COILOVER_LENGTH), 1);
    }
  }

  /** "MBG" with the programme name under it, painted on a vehicle's white side panel. */
  private addLettering(l: { x: number; y: number; z: number; w: number; h: number; ink: string }): void {
    const plate = decalPlane(l.w, l.h, (ctx, w, h) => {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = l.ink;
      ctx.font = `900 ${h * 0.62}px "Arial Black", "Bahnschrift", system-ui, sans-serif`;
      ctx.fillText('MBG', w / 2, h * 0.38, w * 0.9);
      ctx.fillStyle = l.ink === '#ffffff' ? '#ffffff' : '#d3222a';
      ctx.font = `800 ${h * 0.2}px "Arial Narrow", "Bahnschrift", system-ui, sans-serif`;
      ctx.fillText('MAKAN BERGIZI GRATIS', w / 2, h * 0.82, w * 0.94);
    }, 512, Math.round((512 * l.h) / l.w));
    plate.position.set(l.x, l.y, l.z);
    this.chassis.add(plate);
  }

  private addUpgradeParts(parts: UpgradeParts, levels: Readonly<UpgradeLevels>): void {
    const m = this.vehicle.view.mounts;
    const place = (batches: ModelBatch[], mount: { x: number; y: number; z: number; s?: number }) => {
      const part = instantiate(batches);
      part.position.set(mount.x, mount.y, mount.z);
      part.scale.setScalar(mount.s ?? 1);
      this.chassis.add(part);
      return part;
    };
    if (levels.tank >= VISIBLE_AT && m.rack) place(parts.rack, m.rack);
    // The stack's slanted tip is authored facing +X; turn it to point rearward.
    if (levels.engine >= VISIBLE_AT && m.stack) place(parts.stack, m.stack).rotation.y = Math.PI;
    if (levels.engine >= VISIBLE_AT && m.scoop) place(parts.scoop, m.scoop);
    if (levels.suspension >= VISIBLE_AT && m.coilovers) {
      // On the camera side, just inboard of the tyre face, so the spring shows inside the arch.
      const z = Math.max(0, this.vehicle.view.wheelTrackZ - 0.06);
      for (let i = 0; i < 2; i++) this.coilovers[i] = place(parts.coilover, { x: this.vehicle.wheel.axleX[i], y: this.vehicle.wheel.axleY, z });
    }
  }

  /** Red tint on crash; cleared on restart. */
  setCrashed(crashed: boolean): void {
    for (const m of this.flashMaterials) {
      if (!('emissive' in m)) continue;
      if (m.userData.baseEmissive === undefined) {
        m.userData.baseEmissive = m.emissive.getHex();
        m.userData.baseIntensity = m.emissiveIntensity;
      }
      m.emissive.setHex(crashed ? 0x7a1010 : (m.userData.baseEmissive as number));
      m.emissiveIntensity = crashed ? 0.6 : (m.userData.baseIntensity as number);
    }
  }
}

function decalPlane(
  width: number,
  height: number,
  paint: (ctx: CanvasRenderingContext2D, w: number, h: number) => void,
  pxW: number,
  pxH: number,
): THREE.Mesh {
  const canvas = document.createElement('canvas');
  canvas.width = pxW;
  canvas.height = pxH;
  const ctx = canvas.getContext('2d');
  if (ctx) paint(ctx, pxW, pxH);
  const map = new THREE.CanvasTexture(canvas);
  map.colorSpace = THREE.SRGBColorSpace;
  map.anisotropy = 4;
  const material = new THREE.MeshStandardMaterial({
    map,
    transparent: true,
    depthWrite: false,
    roughness: 0.9,
    polygonOffset: true,
    polygonOffsetFactor: -2,
  });
  return new THREE.Mesh(new THREE.PlaneGeometry(width, height), material);
}
